import { EventSource } from "eventsource";
import { getBackendUrl, getHeaders } from "../axios";

/**
 * Isolated Server-Sent Events client for the SSE troubleshooting tool (IDS-7243).
 *
 * Unlike {@link ./EventsHelper}, this helper does NOT use a module-level singleton: each call opens its own
 * {@link EventSource} instance so the diagnostic never touches the live elaboration channel. Auth headers are injected
 * via a custom fetch (same technique as EventsHelper), reusing {@code getHeaders(false)} (no Office SSO token needed).
 *
 * The connection auto-closes on error to prevent the {@code eventsource} library from silently reconnecting, which
 * would corrupt the timing measurements. Callers must also close it when they receive the {@code done} sentinel.
 */

export type DiagnosticSegment = "first-byte" | "stream";

export interface DiagnosticConnectionParams {
  mode: string;
  segment: DiagnosticSegment;
  testId: string;
  step?: number;
}

export interface DiagnosticConnectionCallbacks {
  onOpen: () => void;
  onMessage: (data: unknown) => void;
  onError: (error: unknown) => void;
}

export interface DiagnosticConnectionHandle {
  close: () => void;
}

export const openDiagnosticConnection = async (
  params: DiagnosticConnectionParams,
  callbacks: DiagnosticConnectionCallbacks
): Promise<DiagnosticConnectionHandle> => {
  const query =
    `mode=${encodeURIComponent(params.mode)}` +
    `&segment=${encodeURIComponent(params.segment)}` +
    `&step=${encodeURIComponent(params.step ?? 0)}` +
    `&testId=${encodeURIComponent(params.testId)}`;
  const url = `${getBackendUrl()}/elaboration/status/sse-test?${query}`;

  const headers = await getHeaders(false);

  const eventSource = new EventSource(url, {
    fetch: (input, init) =>
      fetch(input, {
        ...init,
        headers: { ...init.headers, ...headers },
      }),
  });

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    eventSource.onopen = null;
    eventSource.onmessage = null;
    eventSource.onerror = null;
    eventSource.close();
  };

  eventSource.onopen = () => callbacks.onOpen();

  eventSource.onmessage = (event) => {
    try {
      callbacks.onMessage(JSON.parse(event.data));
    } catch {
      callbacks.onError({ type: "parse-error", message: "Failed to parse event data", raw: event.data });
    }
  };

  eventSource.onerror = (event) => {
    // Forward first, then close so the eventsource lib does not auto-reconnect and skew the measurement.
    callbacks.onError(event);
    close();
  };

  return { close };
};
